import { Tooltip } from '@sgme/ui';
import { SpDropdownToggle } from 'components/shared/Dropdown';
import { FormattedMessage } from 'components/shared/Intl';
import { Key, Modifier, Shortcut } from 'components/shared/ShortcutManager';
import { useReadFromClipboard } from 'hooks/useReadFromClipboard';
import { FC, useCallback, useState } from 'react';
import { Dropdown, DropdownHeader, DropdownItem, DropdownMenu } from 'react-bootstrap';

import { connectedBatchToolbarWorkspaceActions } from './BatchToolbarWorkspaceActions.connect';
import type { BatchToolbarWorkspaceActionsProps } from './BatchToolbarWorkspaceActions.models';

export const BatchToolbarWorkspaceActionsComponent: FC<BatchToolbarWorkspaceActionsProps> = ({
  onReset,
  onImported,
  canImportQuotes,
  productType,
  productSubtype,
  wrapper,
}) => {
  const clipboardReader = useReadFromClipboard();
  const isFeatureAvailable = clipboardReader.isAvailable();

  const [isDropdownOpened, setIsDropdownOpened] = useState(false);
  const toggleDropdown = useCallback(() => setIsDropdownOpened(!isDropdownOpened), [isDropdownOpened]);

  const resetWithShortcut = useCallback(() => onReset('shortcut'), [onReset]);
  const resetWithButton = useCallback(() => onReset('button'), [onReset]);

  const onImportButtonClick = useCallback(async () => {
    const identifiers = await clipboardReader.readCellsAsFlatArray(true);
    onImported(identifiers);
  }, [clipboardReader, onImported]);

  const ProductTypeLabel = (
    <div className="d-flex flex-column label fs-6 border-end me-2 pe-2">
      <span data-e2e="batch-product-type">
        <FormattedMessage id={`quote.products.${productType}` as any} />
      </span>
      <div>
        <b data-e2e="batch-product-subtype">
          <FormattedMessage id={`quote.products.${productSubtype}` as any} />
        </b>
        {' - '}
        <b data-e2e="batch-product-wrapper">
          <FormattedMessage id={`quote.products.${wrapper}` as any} />
        </b>
      </div>
    </div>
  );

  return (
    <>
      {/* for md/lg/xl screen */}
      <div className="d-none d-md-flex align-items-center btn-group">
        <Shortcut code={Key.backspace} modifier={Modifier.ctrl} onKeyPress={resetWithShortcut} />
        {ProductTypeLabel}
        <Tooltip position="bottom" contents={<FormattedMessage id="shortcut.CtrlPlusBackspace" />}>
          <div>
            <button
              className="d-none d-xl-flex btn btn-lg btn-flat-primary btn-icon-start align-items-center"
              onClick={resetWithButton}
              type="button"
              data-e2e="batch-reset-tab"
              id="batch-reset-tab"
              aria-label="Reset"
            >
              <i className="d-flex icon icon-sm" aria-hidden="true">
                settings_backup_restore
              </i>
              <FormattedMessage id="reset" />
            </button>
            <button
              className="d-xl-none btn btn-lg btn-flat-primary"
              onClick={resetWithButton}
              type="button"
              data-e2e="batch-reset-tab"
              id="batch-reset-tab"
              aria-label="Reset"
            >
              <i className="d-flex icon icon-sm" aria-hidden="true">
                settings_backup_restore
              </i>
            </button>
          </div>
        </Tooltip>
        {productType && productSubtype && wrapper && (
          <div
            data-e2e={`batch-import-quote-button-tooltip-container`}
            className={isFeatureAvailable ? undefined : 'not-supported-tooltip'}
          >
            <Tooltip
              position="bottom"
              contents={
                <FormattedMessage
                  id={
                    isFeatureAvailable
                      ? 'batchPricer.quote.import.tooltip'
                      : 'batchPricer.quote.import.tooltip.unsupported'
                  }
                />
              }
              color={isFeatureAvailable ? undefined : 'info'}
              data-e2e="batch-import-quote-button-tooltip"
            >
              <div data-e2e="tooltip-trigger">
                <button
                  className="d-none d-xl-flex btn btn-lg btn-flat-primary btn-icon-start align-items-center"
                  type="button"
                  data-e2e="batch-import-quote-button"
                  id="batch-import-quote-button"
                  onClick={onImportButtonClick}
                  disabled={!isFeatureAvailable || !canImportQuotes}
                  aria-label={
                    !isFeatureAvailable
                      ? 'Import quotes from clipboard (not available)'
                      : 'Import quotes from clipboard'
                  }
                >
                  <i className="icon icon-sm" aria-hidden="true">
                    exit_to_app
                  </i>
                  <FormattedMessage id="batchPricer.quote.import" />
                </button>
                <button
                  className="d-xl-none btn btn-lg btn-flat-primary"
                  type="button"
                  data-e2e="batch-import-quote-button"
                  id="batch-import-quote-button"
                  onClick={onImportButtonClick}
                  disabled={!isFeatureAvailable || !canImportQuotes}
                  aria-label={
                    !isFeatureAvailable
                      ? 'Import quotes from clipboard (not available)'
                      : 'Import quotes from clipboard'
                  }
                >
                  <i className="d-flex icon icon-sm" aria-hidden="true">
                    exit_to_app
                  </i>
                </button>
              </div>
            </Tooltip>
          </div>
        )}
      </div>
      {/* xs/sm screen */}
      <div className="d-flex d-md-none align-items-center">
        <Dropdown
          data-e2e="tabs-dropdown"
          show={isDropdownOpened}
          onToggle={toggleDropdown}
          className="border-end pe-2"
        >
          <SpDropdownToggle
            caret={false}
            className="btn-icon btn-lg btn-flat-primary p-0"
            variant="link"
            aria-label={isDropdownOpened ? 'Close actions menu' : 'Open actions menu'}
          >
            <i className="icon" aria-hidden="true">
              info_outline
            </i>
          </SpDropdownToggle>
          <DropdownMenu style={{ minWidth: '200px' }} role="menu">
            <DropdownHeader className="text-primary">{ProductTypeLabel}</DropdownHeader>
            <DropdownItem
              className="d-flex justify-content-between"
              onClick={onImportButtonClick}
              data-e2e="batch-import-quote-button"
              id="batch-import-quote-button"
              disabled={!isFeatureAvailable || !canImportQuotes}
              aria-label={
                isFeatureAvailable ? 'Import quotes from clipboard' : 'Import quotes from clipboard (not available)'
              }
            >
              <FormattedMessage id="batchPricer.quote.import" />
              <i className="d-flex d-xl-none icon icon-sm" aria-hidden="true">
                exit_to_app
              </i>
            </DropdownItem>
            <DropdownItem
              className="d-flex justify-content-between"
              onClick={resetWithButton}
              data-e2e="batch-reset-tab"
              id="batch-reset-tab"
              aria-label="Reset"
            >
              <FormattedMessage id="reset" />
              <i className="d-flex d-xl-none icon icon-sm" aria-hidden="true">
                settings_backup_restore
              </i>
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      </div>
    </>
  );
};

export const BatchToolbarWorkspaceActions = connectedBatchToolbarWorkspaceActions(
  BatchToolbarWorkspaceActionsComponent,
);
